/**
 * 새 소식 — 고객센터 업데이트(data/help/updates.js)를 카드뉴스로 보여 주는 팝업의 규칙.
 *
 * 업데이트에 popup.slides를 넣으면 새 소식이 된다.
 *   popup: {
 *     slides   : [{ image, alt }] 카드뉴스 이미지. alt에는 이미지 속 글자를 그대로 적는다 (필수)
 *     tabLabel : 새 소식이 여러 개일 때 탭 이름 (기본: 업데이트 제목)
 *   }
 * - 업데이트 날짜(KST 0시)부터 WHATS_NEW_DAYS일 동안, 최근 것부터 WHATS_NEW_LIMIT개까지 보인다.
 * - 자동 팝업은 그 업데이트 전에 가입한 로그인 사용자가 아직 못 본 소식이 있을 때 한 번만 뜬다.
 * - 메뉴의 새 소식은 본 적과 상관없이 최근 소식을 다시 보여 준다.
 * - 본 기록은 브라우저에 계정별로 남는다.
 */
import { UPDATES } from '../data/help/updates';
import { isAnnouncementHiddenPath } from './announcements';

const DAY_MS = 24 * 60 * 60 * 1000;
export const WHATS_NEW_DAYS = 60;
export const WHATS_NEW_LIMIT = 7;
const SEEN_LIMIT = 50;
export const WHATS_NEW_CHANGE_EVENT = 'whatsnew:change';
export const WHATS_NEW_OPEN_EVENT = 'whatsnew:open';

// 이미 고객센터에 있거나, 결제·이벤트·채팅·뉴스 읽기처럼 흐름을 끊으면 안 되는 곳
export const WHATS_NEW_EXCLUDE_PATH_PREFIXES = [
  '/help',
  '/chat',
  '/news',
  '/events',
  '/pricing',
  '/subscription',
  '/bulk-purchase',
  '/payment',
  '/billing',
];

const getKstDayStart = (dateString) => new Date(`${dateString}T00:00:00+09:00`).getTime();

const toTimestamp = (value) => (value instanceof Date ? value.getTime() : new Date(value).getTime());

const formatBadge = (timestamp) => {
  const kst = new Date(timestamp + 9 * 60 * 60 * 1000);
  return `${kst.getUTCMonth() + 1}월 ${kst.getUTCDate()}일 업데이트`;
};

export const buildWhatsNewItem = (update) => {
  const slides = (update?.popup?.slides || []).filter(slide => slide?.image && slide?.alt);
  if (slides.length === 0) return null;
  const startsAt = getKstDayStart(update.date);
  if (!Number.isFinite(startsAt)) return null;
  return {
    id: update.id,
    startsAt,
    badge: formatBadge(startsAt),
    title: update.title,
    tabLabel: update.popup.tabLabel || update.title,
    slides,
    detailPath: `/help/notice/${update.id}?from=whats_new`,
  };
};

export const getWhatsNewItems = (now = Date.now(), updates = UPDATES) => {
  const timestamp = toTimestamp(now);
  return updates
    .map(buildWhatsNewItem)
    .filter(item => item && item.startsAt <= timestamp && timestamp < item.startsAt + WHATS_NEW_DAYS * DAY_MS)
    .sort((a, b) => b.startsAt - a.startsAt)
    .slice(0, WHATS_NEW_LIMIT);
};

// 가입 전 업데이트는 그 사용자에게 '바뀐 점'이 아니다. 가입 시각을 모르면 보여 준다.
export const getItemsNewToAccount = (items, accountCreatedAt) => {
  const createdAt = toTimestamp(accountCreatedAt);
  if (!Number.isFinite(createdAt)) return items;
  return items.filter(item => item.startsAt > createdAt);
};

export const isWhatsNewBlockedPath = (pathname = '') => (
  isAnnouncementHiddenPath(pathname)
  || WHATS_NEW_EXCLUDE_PATH_PREFIXES.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))
);

export const getWhatsNewSeenKey = (uid) => `whatsnew_seen_ids:${uid}`;

export const readSeenIds = (uid) => {
  if (!uid) return [];
  try {
    const parsed = JSON.parse(localStorage.getItem(getWhatsNewSeenKey(uid)) || '[]');
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
  } catch {
    return [];
  }
};

export const markWhatsNewSeen = (uid, ids) => {
  if (!uid || !ids?.length) return;
  const seen = readSeenIds(uid);
  const next = [...ids.filter(id => !seen.includes(id)), ...seen].slice(0, SEEN_LIMIT);
  try {
    localStorage.setItem(getWhatsNewSeenKey(uid), JSON.stringify(next));
  } catch {
    return;
  }
  window.dispatchEvent(new Event(WHATS_NEW_CHANGE_EVENT));
};

// 로그인 사용자에게 아직 보여 주지 않은 새 소식
export const getUnseenWhatsNew = ({ uid, accountCreatedAt, now = Date.now(), updates = UPDATES }) => {
  if (!uid) return [];
  const seen = readSeenIds(uid);
  return getItemsNewToAccount(getWhatsNewItems(now, updates), accountCreatedAt)
    .filter(item => !seen.includes(item.id));
};

export const openWhatsNew = () => window.dispatchEvent(new Event(WHATS_NEW_OPEN_EVENT));
